import { PhonemeStats } from './phonemeAnalysis';
import { TranscriptionResult } from './speechRecognition';
import { PerformanceLogger } from './performanceLogger';

export interface AudioStats {
  duration: number;
  averagePitch: number;
  pitchRange: { min: number; max: number };
  averageVolume: number;
  volumeRange: { min: number; max: number };
  silenceRatio: number;
  estimatedWordsPerMinute: number;
  pauseCount: number;
  phonemeStats: PhonemeStats;
  transcription: TranscriptionResult;
}

export class AudioAnalyzer {
  private audioContext: AudioContext;
  private worker: Worker | null = null;
  
  constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.initializeWorker();
  }
  
  private initializeWorker() {
    try {
      this.worker = new Worker(new URL('../workers/audioAnalysisWorker.ts', import.meta.url));
      console.log('Web Worker initialized successfully');
    } catch (error) {
      console.warn('Web Worker initialization failed:', error);
      console.warn('Falling back to main thread processing (simplified phoneme analysis)');
      this.worker = null;
    }
  }
  
  async analyzeAudio(audioBlob: Blob, enablePhonemeAnalysis: boolean = false, webSpeechTranscript?: any): Promise<AudioStats> {
    console.log('audioAnalysis.analyzeAudio called');
    console.log('audioAnalysis - webSpeechTranscript parameter:', webSpeechTranscript);
    console.log('audioAnalysis - webSpeechTranscript type:', typeof webSpeechTranscript);
    console.log('audioAnalysis - webSpeechTranscript truthy?', !!webSpeechTranscript);
    
    const perfLogger = PerformanceLogger.getInstance();
    
    try {
      // Start audio processing timing
      perfLogger.startTiming('audioProcessing');
      
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;
      const duration = audioBuffer.duration;
      
      // FAST: Basic volume calculation (simplified)
      const volumeStats = this.calculateVolumeStatsFast(channelData);
      
      // FAST: Basic pitch detection (simplified) 
      const pitchStats = this.calculatePitchStatsFast(channelData, sampleRate);
      
      // FAST: Quick silence detection
      const silenceAnalysis = this.analyzeSilenceFast(channelData, sampleRate);
      
      // Estimate words per minute
      const estimatedWordsPerMinute = this.estimateWordsPerMinute(
        duration,
        silenceAnalysis.silenceRatio
      );
      
      perfLogger.endTiming('audioProcessing');
      
      // SKIP PHONEME ANALYSIS BY DEFAULT (saves 4-8 seconds!)
      let phonemeStats;
      if (enablePhonemeAnalysis) {
        perfLogger.startTiming('phonemeAnalysis');
        phonemeStats = await this.analyzePhonemes(channelData, sampleRate);
        perfLogger.endTiming('phonemeAnalysis');
      } else {
        // Return mock phoneme stats for UI compatibility
        phonemeStats = this.getMockPhonemeStats();
      }
      
      // Use Web Speech API transcript if available, otherwise fall back to energy-based detection
      perfLogger.startTiming('transcription');
      console.log('Web Speech Transcript received:', webSpeechTranscript);
      console.log('Web Speech Transcript type:', typeof webSpeechTranscript);
      console.log('Web Speech Transcript properties:', webSpeechTranscript ? Object.keys(webSpeechTranscript) : 'null');
      
      const transcription = webSpeechTranscript 
        ? this.formatWebSpeechTranscript(webSpeechTranscript, duration)
        : this.getQuickTranscription(channelData, sampleRate, duration);
      console.log('Final transcription object:', transcription);
      console.log('Final transcription text:', transcription.transcript);
      perfLogger.endTiming('transcription');
      
      // Calculate final stats
      perfLogger.startTiming('statsCalculation');
      
      // Update estimated WPM
      const actualSpeakingTime = duration * (1 - silenceAnalysis.silenceRatio);
      const calculatedWPM = transcription.wordCount > 0 && actualSpeakingTime > 0
        ? Math.round((transcription.wordCount / actualSpeakingTime) * 60)
        : estimatedWordsPerMinute;
      
      perfLogger.endTiming('statsCalculation');
      
      return {
        duration: Math.round(duration * 100) / 100,
        averagePitch: pitchStats.average,
        pitchRange: pitchStats.range,
        averageVolume: volumeStats.average,
        volumeRange: volumeStats.range,
        silenceRatio: silenceAnalysis.silenceRatio,
        estimatedWordsPerMinute: calculatedWPM,
        pauseCount: silenceAnalysis.pauseCount,
        phonemeStats,
        transcription
      };
    } catch (error) {
      console.error('Error analyzing audio:', error);
      throw error;
    }
  }
  
  private async analyzePhonemes(channelData: Float32Array, sampleRate: number): Promise<PhonemeStats> {
    if (this.worker) {
      console.log('Using Web Worker for phoneme analysis (detailed, 4-8 seconds)');
      return new Promise((resolve, reject) => {
        const handleMessage = (e: MessageEvent) => {
          const { type, data, error } = e.data;
          
          if (type === 'ANALYSIS_COMPLETE') {
            this.worker!.removeEventListener('message', handleMessage);
            resolve(data);
          } else if (type === 'ANALYSIS_ERROR') {
            this.worker!.removeEventListener('message', handleMessage);
            reject(new Error(error));
          }
          // Progress updates are handled in the component
        };
        
        this.worker!.addEventListener('message', handleMessage);
        this.worker!.postMessage({
          type: 'ANALYZE_AUDIO',
          audioData: channelData,
          sampleRate
        });
      });
    } else {
      // Fallback to simplified main-thread analysis
      console.warn('Using fallback phoneme analysis (simplified, fast but less accurate)');
      return this.fallbackPhonemeAnalysis(channelData, sampleRate);
    }
  }
  
  private fallbackPhonemeAnalysis(channelData: Float32Array, sampleRate: number): PhonemeStats {
    // Simplified analysis for fallback
    
    // Very basic vowel/consonant estimation based on energy distribution
    let lowEnergyFrames = 0;
    let highEnergyFrames = 0;
    const frameSize = 1024;
    
    for (let i = 0; i < channelData.length - frameSize; i += frameSize) {
      const frame = channelData.slice(i, i + frameSize);
      const energy = frame.reduce((sum, val) => sum + val * val, 0) / frameSize;
      
      if (energy > 0.01) {
        // Simple heuristic: higher energy often correlates with consonants
        if (energy > 0.05) {
          highEnergyFrames++;
        } else {
          lowEnergyFrames++;
        }
      }
    }
    
    const totalFrames = lowEnergyFrames + highEnergyFrames;
    const vowelPercentage = totalFrames > 0 ? Math.round((lowEnergyFrames / totalFrames) * 100) : 50;
    
    return {
      vowelPercentage,
      consonantPercentage: 100 - vowelPercentage,
      fricativeCount: Math.round(highEnergyFrames * 0.3),
      stopCount: Math.round(highEnergyFrames * 0.4),
      nasalCount: Math.round(highEnergyFrames * 0.3),
      clarity: 0.7,
      dominantFormants: { f1: 500, f2: 1500, f3: 2500 },
      difficultPhonemes: vowelPercentage < 40 ? ['Vowel clarity'] : vowelPercentage > 70 ? ['Consonant precision'] : [],
      recommendations: ['Continue practicing for better speech clarity and balance']
    };
  }
  
  // FAST versions of analysis methods (10x faster)
  private calculateVolumeStatsFast(channelData: Float32Array): {
    average: number;
    range: { min: number; max: number };
  } {
    // Sample only every 100th point for speed
    const sampleRate = 100;
    let sum = 0;
    let min = 1;
    let max = 0;
    let count = 0;
    
    for (let i = 0; i < channelData.length; i += sampleRate) {
      const amplitude = Math.abs(channelData[i]);
      sum += amplitude;
      min = Math.min(min, amplitude);
      max = Math.max(max, amplitude);
      count++;
    }
    
    return {
      average: Math.round((sum / count) * 100),
      range: {
        min: Math.round(min * 100),
        max: Math.round(max * 100)
      }
    };
  }
  
  private calculatePitchStatsFast(channelData: Float32Array, sampleRate: number): {
    average: number;
    range: { min: number; max: number };
  } {
    // Very simplified pitch estimation - just return typical values
    // Real pitch detection is too slow for real-time
    const energyLevel = channelData.reduce((sum, val) => sum + Math.abs(val), 0) / channelData.length;
    
    // Estimate pitch based on energy (rough approximation)
    const basePitch = 100 + (energyLevel * 500);
    
    return {
      average: Math.round(basePitch),
      range: { 
        min: Math.round(basePitch * 0.8), 
        max: Math.round(basePitch * 1.2) 
      }
    };
  }
  
  private analyzeSilenceFast(channelData: Float32Array, sampleRate: number): {
    silenceRatio: number;
    pauseCount: number;
  } {
    const threshold = 0.01;
    const windowSize = Math.floor(sampleRate * 0.1); // 100ms windows
    let silentWindows = 0;
    let totalWindows = 0;
    let pauseCount = 0;
    let inSilence = false;
    
    // Sample less frequently for speed
    for (let i = 0; i < channelData.length - windowSize; i += windowSize) {
      const windowSlice = channelData.slice(i, i + Math.min(100, windowSize));
      let windowMax = 0;
      for (let j = 0; j < windowSlice.length; j++) {
        windowMax = Math.max(windowMax, Math.abs(windowSlice[j]));
      }
      totalWindows++;
      
      if (windowMax < threshold) {
        silentWindows++;
        if (!inSilence) {
          pauseCount++;
          inSilence = true;
        }
      } else {
        inSilence = false;
      }
    }
    
    return {
      silenceRatio: Math.round((silentWindows / totalWindows) * 100) / 100,
      pauseCount
    };
  }
  
  private formatWebSpeechTranscript(webSpeechResult: any, duration: number): TranscriptionResult {
    // Format Web Speech API result into our TranscriptionResult format
    const transcript = webSpeechResult.transcript || '';
    const words = webSpeechResult.words || [];
    const wordCount = words.length;
    const confidence = webSpeechResult.confidence || 0.9;
    const wordsWithTimestamps = webSpeechResult.wordsWithTimestamps || null;
    
    return {
      transcript: transcript,
      words: words,
      wordCount: wordCount,
      confidence: confidence,
      alternativeTranscripts: [],
      wordsWithTimestamps: wordsWithTimestamps
    };
  }
  
  private getQuickTranscription(channelData: Float32Array, sampleRate: number, duration: number): TranscriptionResult {
    // Quick word estimation without heavy processing
    const silenceThreshold = 0.01;
    const minWordDuration = 0.15; // 150ms minimum for a word
    
    let wordCount = 0;
    let inWord = false;
    let wordStartTime = 0;
    
    const windowSize = Math.floor(sampleRate * 0.02); // 20ms windows
    
    for (let i = 0; i < channelData.length; i += windowSize) {
      const windowEnergy = Math.abs(channelData[i]);
      const currentTime = i / sampleRate;
      
      if (windowEnergy > silenceThreshold && !inWord) {
        inWord = true;
        wordStartTime = currentTime;
      } else if (windowEnergy <= silenceThreshold && inWord) {
        const wordDuration = currentTime - wordStartTime;
        if (wordDuration >= minWordDuration) {
          wordCount++;
        }
        inWord = false;
      }
    }
    
    // Generate more realistic placeholder transcript
    // Since we don't have real speech-to-text, we'll indicate this clearly
    const placeholderMessage = `[Audio detected ${wordCount} speech segments. Real-time transcription requires speech-to-text API integration.]`;
    
    return {
      transcript: placeholderMessage,
      words: [], // Empty array since we don't have real words
      wordCount,
      confidence: 0.7,
      alternativeTranscripts: []
    };
  }
  
  private getMockPhonemeStats(): PhonemeStats {
    // Return placeholder stats for UI compatibility
    return {
      vowelPercentage: 45,
      consonantPercentage: 55,
      fricativeCount: 10,
      stopCount: 12,
      nasalCount: 8,
      clarity: 0.75,
      dominantFormants: { f1: 500, f2: 1500, f3: 2500 },
      difficultPhonemes: [],
      recommendations: ['Quick analysis mode - phoneme analysis disabled for speed']
    };
  }
  
  private calculateVolumeStats(channelData: Float32Array): {
    average: number;
    range: { min: number; max: number };
  } {
    let sum = 0;
    let min = 1;
    let max = 0;
    
    // Calculate RMS (Root Mean Square) for average volume
    for (let i = 0; i < channelData.length; i++) {
      const amplitude = Math.abs(channelData[i]);
      sum += amplitude * amplitude;
      min = Math.min(min, amplitude);
      max = Math.max(max, amplitude);
    }
    
    const rms = Math.sqrt(sum / channelData.length);
    
    return {
      average: Math.round(rms * 1000) / 10, // Convert to percentage
      range: {
        min: Math.round(min * 1000) / 10,
        max: Math.round(max * 1000) / 10
      }
    };
  }
  
  private calculatePitchStats(channelData: Float32Array, sampleRate: number): {
    average: number;
    range: { min: number; max: number };
  } {
    // Simple autocorrelation-based pitch detection
    const windowSize = 4096;
    const pitchValues: number[] = [];
    
    for (let i = 0; i < channelData.length - windowSize; i += windowSize / 2) {
      const window = channelData.slice(i, i + windowSize);
      const pitch = this.detectPitch(window, sampleRate);
      if (pitch > 0) {
        pitchValues.push(pitch);
      }
    }
    
    if (pitchValues.length === 0) {
      return { average: 0, range: { min: 0, max: 0 } };
    }
    
    const average = pitchValues.reduce((a, b) => a + b, 0) / pitchValues.length;
    const min = Math.min(...pitchValues);
    const max = Math.max(...pitchValues);
    
    return {
      average: Math.round(average),
      range: { min: Math.round(min), max: Math.round(max) }
    };
  }
  
  private detectPitch(buffer: Float32Array, sampleRate: number): number {
    // Simple autocorrelation method for pitch detection
    const minPeriod = Math.floor(sampleRate / 500); // 500 Hz max
    const maxPeriod = Math.floor(sampleRate / 50);  // 50 Hz min
    
    let maxCorrelation = 0;
    let bestPeriod = 0;
    
    for (let period = minPeriod; period < maxPeriod && period < buffer.length; period++) {
      let correlation = 0;
      for (let i = 0; i < buffer.length - period; i++) {
        correlation += Math.abs(buffer[i] * buffer[i + period]);
      }
      
      if (correlation > maxCorrelation) {
        maxCorrelation = correlation;
        bestPeriod = period;
      }
    }
    
    if (bestPeriod === 0) return 0;
    
    return sampleRate / bestPeriod;
  }
  
  private analyzeSilence(channelData: Float32Array, sampleRate: number): {
    silenceRatio: number;
    pauseCount: number;
  } {
    const silenceThreshold = 0.01;
    const minPauseDuration = 0.2; // 200ms minimum for a pause
    const windowSize = Math.floor(sampleRate * 0.05); // 50ms windows
    
    let silentWindows = 0;
    let totalWindows = 0;
    let pauseCount = 0;
    let currentSilenceDuration = 0;
    
    for (let i = 0; i < channelData.length - windowSize; i += windowSize) {
      const window = channelData.slice(i, i + windowSize);
      const windowRMS = Math.sqrt(
        window.reduce((sum, val) => sum + val * val, 0) / windowSize
      );
      
      totalWindows++;
      
      if (windowRMS < silenceThreshold) {
        silentWindows++;
        currentSilenceDuration += windowSize / sampleRate;
      } else {
        if (currentSilenceDuration >= minPauseDuration) {
          pauseCount++;
        }
        currentSilenceDuration = 0;
      }
    }
    
    // Check for final pause
    if (currentSilenceDuration >= minPauseDuration) {
      pauseCount++;
    }
    
    return {
      silenceRatio: Math.round((silentWindows / totalWindows) * 100) / 100,
      pauseCount
    };
  }
  
  private estimateWordsPerMinute(duration: number, silenceRatio: number): number {
    // Rough estimation based on average speaking rate
    // Average person speaks 150-160 words per minute
    // Adjust based on silence ratio
    const speakingTime = duration * (1 - silenceRatio);
    const estimatedWords = (speakingTime / 60) * 150;
    
    return Math.round(estimatedWords / (duration / 60));
  }
}